import { useApolloClient } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useElements, useStripe } from '@stripe/react-stripe-js'

import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import {
  PaymentOrderDocument,
  PaymentOrderQuery,
  usePaymentGetMarketplaceFeeLazyQuery,
  usePaymentTrackCheckoutCompleteMutation,
} from '../../graphql'
import useCheckoutContext from '../../data/useCheckoutContext'
import useOrder from '../../data/useOrder'
import getPhoenixUrlParams, { clearPaymentUrlParams } from '../../lib/get-phoenix-url-params'
import { useIntl } from '../../localization/react'
import { OrderState } from '../../typings/Order'
import { PaymentMethodVerificationMethod, PaymentState, ProcessPaymentOptions } from '../../typings/Payment'

import getUserTrackingPreferences from '../../data/get-user-tracking-preferences'
import sessionUtils from '../../data/session-utils'

import checkIfAdvertisingTrackingAllowed from '../../lib/check-if-advertising-tracking-allowed'
import handlePaymentError from './handle-payment-error'
import PaymentForm from './PaymentForm'
import pollPayment from './poll-payment'
import processPayment from './process-payment'
import confirmOrder from './confirm-order'
import SkeletonPayment from './Skeletons/SkeletonPayment'
import useGooglePurcahseTracking from './hooks/useGooglePurcahseTracking'

const hasIdealVerification = (
  paymentMethods: Array<{ verifyWith: PaymentMethodVerificationMethod | null }>,
): boolean => {
  const verificationMethods = paymentMethods.map((pm) => pm.verifyWith?.toString())
  return verificationMethods.includes(PaymentMethodVerificationMethod.PAYMENT_METHOD_IDEAL)
}

const Payment = () => {
  const intl = useIntl()
  const stripe = useStripe()
  const history = useHistory()
  const location = useLocation()
  const context = useCheckoutContext()
  const elements = useElements()
  const apolloClient = useApolloClient()
  const { amplitude, deviceId, sessionId } = useAmplitude()

  const { orderId, paymentInProgress, isSetupFlow, lastPaymentAttempt = null } = getPhoenixUrlParams(location.search)
  const { orderData, isOrderLoading } = useOrder<PaymentOrderQuery>(PaymentOrderDocument)
  const [savedPaymentMethodId, setSavedPaymentMethodId] = useState<string | undefined>()
  const [paymentWasSuccessful, setPaymentWasSuccessful] = useState(false)
  const [hasHandledGoogleTracking, setHasHandledGoogleTracking] = useState(false)

  const isAdvertisingTrackingAllowed = checkIfAdvertisingTrackingAllowed()
  const [trackCheckoutComplete] = usePaymentTrackCheckoutCompleteMutation()
  const [getMarketplaceFee, { data: marketplaceFeeData }] = usePaymentGetMarketplaceFeeLazyQuery()
  const { handleGooglePurchaseTracking } = useGooglePurcahseTracking()
  const [showVerificationTransactionFeeDisclaimer, setShowVerificationTransactionFeeDisclaimer] = useState(false)

  const trackGooglePurchase = async ({ orderId }: { orderId: string }) => {
    await handleGooglePurchaseTracking({ orderId })
    setHasHandledGoogleTracking(true)
  }

  const handleSelectedPaymentMethodChange = ({
    isValid,
    paymentMethodId,
  }: {
    isValid: boolean
    paymentMethodId?: string
  }) => {
    setSavedPaymentMethodId(paymentMethodId)
    if (isValid) {
      context.setIsPaymentValid(true)
    } else {
      context.setIsPaymentValid(false)
    }
  }

  const pollPaymentStatus = async () => {
    const additionalExpectedPaymentStates = isSetupFlow
      ? [PaymentState.PAYMENT_STATE_ON_HOLD, PaymentState.PAYMENT_STATE_IN_PROGRESS]
      : undefined

    // payments that lead you to external websites have the (`lastPaymentAttempt`) attached to the returnUrl as we have to keep the during the `redirect`
    const { wasSuccessful, error } = await pollPayment(
      { apolloClient },
      { orderId: orderId!, lastPaymentAttempt, additionalExpectedPaymentStates },
    )

    if (wasSuccessful) {
      setPaymentWasSuccessful(true)
    } else if (error) {
      sessionUtils.removeSetupIntentClientSecret()
      history.replace({ pathname: location.pathname, search: clearPaymentUrlParams(location.search) })
      context.setIsPaymentProcessing(false)
      handlePaymentError(error, context.setError, intl)
    }
  }

  // Once the marketplace fee data is returned, we can log the mpf
  useEffect(() => {
    if (!marketplaceFeeData) return

    if (marketplaceFeeData.getMarketplaceFeeForOrder) {
      window.dataLayer.push({
        event: 'phoenix-mpf-tracking',
        mpfv: marketplaceFeeData.getMarketplaceFeeForOrder.value / 100,
        mpfc: marketplaceFeeData.getMarketplaceFeeForOrder.currency,
      })
    }
  }, [marketplaceFeeData])

  // Once the google event is logged, we can push the user to the confirmation page
  useEffect(() => {
    if (!hasHandledGoogleTracking || !marketplaceFeeData) return
    context.setIsPaymentProcessing(false)
    history.push({ pathname: context.getNextStep(), search: location.search })
  }, [hasHandledGoogleTracking, marketplaceFeeData])

  useEffect(() => {
    if (!orderData?.order.payment?.canSetupRecurringPaymentMethod) {
      return
    }

    setShowVerificationTransactionFeeDisclaimer(hasIdealVerification(orderData.order.payment.paymentMethods))
  }, [orderData?.order.payment?.paymentMethods, orderData?.order.payment?.canSetupRecurringPaymentMethod])

  // Sync UI when coming back from an external payment provider
  useEffect(() => {
    if (paymentInProgress) {
      context.setIsPaymentProcessing(true)
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      pollPaymentStatus()
    }
  }, [])

  // Sync processing payment function according to the selected payment method
  useEffect(() => {
    if (!orderData) return

    if (!orderData.order.payment) {
      // If offline payment is the only available payment method
      if (orderData.order.isConfirmable) {
        context.setProcessPayment(() => async () => confirmOrder({ apolloClient }, orderId!))
      }

      return
    }

    const paymentToProcess: ProcessPaymentOptions = {
      payment: orderData.order.payment,
      orderId: orderId!,
      user: orderData.order.payment.user,
      savedPaymentMethodId,
      elements,
    }

    // Setting a function in state with useState() needs to be handled this way
    // https://stackoverflow.com/a/55621325
    context.setProcessPayment(() => async () => processPayment({ stripe }, { apolloClient }, paymentToProcess))
  }, [orderData, stripe, savedPaymentMethodId, elements])

  useEffect(() => {
    if (!paymentWasSuccessful) return
    const { isTrackingAllowed } = getUserTrackingPreferences()

    if (!isTrackingAllowed && !isAdvertisingTrackingAllowed) {
      history.push({ pathname: context.getNextStep(), search: location.search })
      return
    }

    if (paymentWasSuccessful && orderId && isTrackingAllowed) {
      if (deviceId && sessionId && orderId) {
        trackCheckoutComplete({
          variables: { orderId, deviceId, sessionId: Number(sessionId) },
        })
      }
      getMarketplaceFee({ variables: { orderId } })
    }

    // track the google purchase event
    const hasGlParam = window.location.href.includes('_gl')
    if (hasGlParam && isAdvertisingTrackingAllowed && !hasHandledGoogleTracking && orderId) {
      trackGooglePurchase({ orderId })
    } else {
      setHasHandledGoogleTracking(true)
    }
  }, [paymentWasSuccessful, amplitude])

  if (isOrderLoading || !orderData) {
    return <SkeletonPayment />
  }

  return (
    <PaymentForm
      isOrderConfirmed={orderData.order.state === OrderState.ORDER_STATE_CONFIRMED}
      isPaymentProcessing={context.isPaymentProcessing}
      handleSelectedPaymentMethodChange={handleSelectedPaymentMethodChange}
      showVerificationTransactionFeeDisclaimer={showVerificationTransactionFeeDisclaimer}
    />
  )
}

export default Payment
